import { SPACING as BASE_SPACING } from "@/config/layout";
import { cn } from "@/lib/utils";

export const SPACING = {
  PAGE_PADDING: BASE_SPACING.PAGE_PADDING,
  CARD_PADDING: BASE_SPACING.CARD_PADDING,
  CARD_HEADER_PADDING: BASE_SPACING.CARD_HEADER_PADDING,
  CARD_CONTENT_PADDING: BASE_SPACING.CARD_CONTENT_PADDING,
  TABLE_CELL: BASE_SPACING.TABLE_CELL,
  CONTROL_PAD: BASE_SPACING.CONTROL_PAD,
  CONTROL_PAD_X: BASE_SPACING.CONTROL_PAD_X,
  CONTROL_PAD_Y: BASE_SPACING.CONTROL_PAD_Y,
  SECTION_GAP: BASE_SPACING.SECTION_GAP,
  SECTION_GAP_LARGE: BASE_SPACING.SECTION_GAP_LARGE,
  GAP_TIGHT: BASE_SPACING.GAP_TIGHT,
  GAP_SM: BASE_SPACING.GAP_SM,
  GAP_DEFAULT: BASE_SPACING.GAP_DEFAULT,
  GAP_LARGE: BASE_SPACING.GAP_LARGE,
  MARGIN_TOP_TINY: BASE_SPACING.MARGIN_TOP_TINY,
  MARGIN_TOP_TIGHT: BASE_SPACING.MARGIN_TOP_TIGHT,
  MARGIN_BOTTOM: BASE_SPACING.MARGIN_BOTTOM,
  MARGIN_BOTTOM_LARGE: BASE_SPACING.MARGIN_BOTTOM_LARGE,
  DASHBOARD_CARD_MIN_H: BASE_SPACING.DASHBOARD_CARD_MIN_H,
  HEADER_PADDING: BASE_SPACING.HEADER_PADDING,
  FILTER_CONTROL_MIN_W: BASE_SPACING.FILTER_CONTROL_MIN_W,
  FILTER_CONTROL_MIN_W_WIDE: BASE_SPACING.FILTER_CONTROL_MIN_W_WIDE,
  TASK_BOARD_VIEWPORT_H: BASE_SPACING.TASK_BOARD_VIEWPORT_H,
  TASK_BOARD_COLUMN: BASE_SPACING.TASK_BOARD_COLUMN,
  TASK_BOARD_COLUMN_WIDTH_PX: BASE_SPACING.TASK_BOARD_COLUMN_WIDTH_PX,
  TASK_BOARD_COLUMN_GAP_PX: BASE_SPACING.TASK_BOARD_COLUMN_GAP_PX,
  TASK_BOARD_SCROLLBAR: BASE_SPACING.TASK_BOARD_SCROLLBAR,
  TASK_BOARD_SCROLL_TRACK: BASE_SPACING.TASK_BOARD_SCROLL_TRACK,
  TASK_BOARD_SCROLL_THUMB: BASE_SPACING.TASK_BOARD_SCROLL_THUMB,
  TASK_BOARD_EMPTY_PAD: BASE_SPACING.TASK_BOARD_EMPTY_PAD,
  TASK_BOARD_SCROLL_BTN_PAD: BASE_SPACING.TASK_BOARD_SCROLL_BTN_PAD,
  SECTION_RULE_PAD_T: BASE_SPACING.SECTION_RULE_PAD_T,
  SECTION_META_PAD_B: BASE_SPACING.SECTION_META_PAD_B,
  SECTION_META_GAP: BASE_SPACING.SECTION_META_GAP,
  CHIP_PAD: BASE_SPACING.CHIP_PAD,
  CHIP_PAD_TIGHT: BASE_SPACING.CHIP_PAD_TIGHT,
  CHIP_PAD_MD: BASE_SPACING.CHIP_PAD_MD,
  CONTROL_HEIGHT_SM: BASE_SPACING.CONTROL_HEIGHT_SM,
  CONTROL_HEIGHT_SM_MIN: BASE_SPACING.CONTROL_HEIGHT_SM_MIN,
  CONTROL_HEIGHT_XS: BASE_SPACING.CONTROL_HEIGHT_XS,
  AVATAR_MICRO: BASE_SPACING.AVATAR_MICRO,
  ICON_BTN_PAD: BASE_SPACING.ICON_BTN_PAD,
  COLOR_SWATCH_W: BASE_SPACING.COLOR_SWATCH_W,
  WORKSPACE_FILL_HEIGHT: "min-h-[calc(100vh-9rem)]",
  /** List tables: measured fill-to-bottom when set; else static hub chrome reserve. */
  WORKSPACE_LIST_TABLE_SCROLL_MAX_H: "h-[var(--hub-table-available-height,auto)] max-h-[var(--hub-table-available-height,calc(100dvh-13.5rem))] overflow-hidden [contain:layout_paint]",
  WORKSPACE_LIST_TABLE_FRAME: "min-h-0 min-w-0 flex-none",
  /** List page table + pagination stack (Records, Inbox, Tasks): clip table scroll bleed. */
  WORKSPACE_LIST_SECTION_FRAME: "min-h-0 flex flex-col overflow-hidden [contain:layout_paint]",
  /** Embedded panel tables (project detail, task time log, digest): cap scroll height. */
  WORKSPACE_PANEL_TABLE_SCROLL_MAX_H: "max-h-[min(60vh,32rem)]",
  /** Dashboard widget tables inside fixed-height cards (~300px). */
  WORKSPACE_DASHBOARD_TABLE_SCROLL_MAX_H: "max-h-[280px]",
  /** Sticky section headers below {@link APP_SHELL.headerBar} (`h-14`). */
  WORKSPACE_STICKY_BELOW_HEADER: "top-14",
  WORKSPACE_STICKY_SECTION_HEADER_Z: "z-30",
} as const;

/** Pagination + optional list KPI under table footer — single SoT (also listKpiChrome.footerStack). */
export const LIST_TABLE_FOOTER_STACK = "flex min-w-0 flex-col gap-1.5";

/** Hub / Finance / Contracts / Content / Work list pages share this chrome (Invoices is the reference). */
export const WORKSPACE_LIST_PAGE = {
  MAIN: cn("flex min-h-0 min-w-0 flex-col overflow-x-clip", BASE_SPACING.SECTION_GAP_LARGE),
  SECTION: "flex min-h-0 min-w-0 flex-col overflow-x-clip",
  BODY: cn("flex min-h-0 min-w-0 flex-col px-0 py-0 workspace-scroll-contain", BASE_SPACING.GAP_DEFAULT),
  BODY_TEST_ID: "workspace-list-body",
  /** Title + primary actions. No bottom rule — table frame / filter band own the divider. */
  HEADER_ROW: "flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between",
  /** List filter row only: search / one decide + overflow + labeled sm actions. Never KPI, dashboard rail, or a second row. */
  FILTER_BAND: cn("flex min-w-0 flex-col border-b border-border pb-2", BASE_SPACING.GAP_DEFAULT),
  FILTER_ROW:
    "flex min-w-0 max-w-full snap-x snap-mandatory items-center gap-x-1.5 gap-y-1.5 overflow-x-auto py-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:flex-wrap sm:overflow-visible sm:snap-none",
  DISCOVERY_SEARCH_INPUT:
    "auth-input-gold h-12 w-full min-w-0 rounded-full border-border surface-card pl-11 pr-28 text-sm shadow-elevation1 transition-shadow hover:shadow-md focus-visible:ring-1 focus-visible:ring-[rgb(var(--nice-gold-rgb)/0.45)] sm:pr-32",
  DISCOVERY_SEARCH_SUBMIT: "absolute right-1.5 top-1/2 h-8 min-w-[4.5rem] -translate-y-1/2 gap-1.5 rounded-full px-3 text-xs",
  TABLE_LIST_STACK: cn("min-h-0 min-w-0 flex flex-col overflow-x-clip", SPACING.WORKSPACE_LIST_SECTION_FRAME),
  TABLE_SCROLL: cn(SPACING.WORKSPACE_LIST_TABLE_FRAME, SPACING.WORKSPACE_LIST_TABLE_SCROLL_MAX_H),
  /**
   * Mandatory outer wrap for Invoices-parity hub lists: filter band + alerts + table stack.
   * Same class as LIST_FILTER_SECTION. Escape hatches (detailOnly, TokenTable leaf, etc.) stay explicit — do not invent a second stack.
   */
  FILTER_SECTION: cn("flex min-w-0 flex-col overflow-x-clip", SPACING.GAP_SM),
  /** Pagination inside hub ScrollableTable footer — flush, no double border. */
  TABLE_PAGINATION_FOOTER: "mt-0 border-t-0 pt-0",
  /** Alias of {@link LIST_TABLE_FOOTER_STACK} — prefer that or listKpiChrome.footerStack at call sites. */
  TABLE_FOOTER_STACK: LIST_TABLE_FOOTER_STACK,
  EDITOR_GRID_SCROLL: "table-surface-frame max-h-[68vh] min-w-0 max-w-full overflow-auto rounded-md border workspace-scroll-contain",
} as const;

/** Base Style rail tokens — ModuleRails + Sidebar module-section/expand leaves (DEC-P0-FE-MODULE-RAILS-002/003). */
export const MODULE_RAILS = {
  BAND: cn("flex min-w-0 flex-col", BASE_SPACING.GAP_DEFAULT),
  /** Shared white shelf for L2 + L3 (Base Style — never leave L3 bare on canvas). */
  L2_SHELF:
    "overflow-hidden rounded-md border border-border bg-card dark:border-border/50 dark:bg-muted/20",
  ROW: cn(
    "flex min-w-0 max-w-full flex-nowrap items-center gap-x-0 overflow-x-auto",
    "[scrollbar-width:thin] [&::-webkit-scrollbar]:h-1",
  ),
  L2_TAB: cn(
    "relative inline-flex h-9 shrink-0 items-center px-3 text-sm font-medium tracking-tight transition-colors",
    "after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-transparent",
  ),
  L2_TAB_ON: "bg-primary/10 text-primary font-semibold after:bg-primary",
  L2_TAB_OFF: "text-muted-foreground hover:bg-primary/5 hover:text-foreground",
  /** Divider when L3 sits under L2/subgroup inside the same shelf. */
  L3_TRACK: "border-t border-border/70",
  L3_LINK: cn(
    "relative inline-flex h-7 shrink-0 items-center px-2.5 text-xs font-medium transition-colors",
    "after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-transparent",
  ),
  L3_LINK_ON: "bg-primary/5 text-primary font-medium after:bg-primary",
  L3_LINK_OFF: "text-muted-foreground hover:bg-primary/5 hover:text-foreground",
  BODY_SPLIT: "flex min-h-0 min-w-0 flex-1 gap-3",
  L3_ASIDE: cn(
    "flex w-52 shrink-0 flex-col overflow-hidden rounded-md border border-border bg-card dark:border-border/50 dark:bg-muted/20",
  ),
  L3_ASIDE_SCROLL: cn(
    "flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto p-1.5",
    "[scrollbar-width:thin]",
  ),
  L3_ASIDE_LEADING: "sticky top-0 z-10 shrink-0 border-b border-border/70 bg-card p-1.5 dark:bg-muted/20",
  L3_VERTICAL_LINK: cn(
    "flex w-full items-center rounded-md px-2.5 py-1.5 text-left text-xs font-medium transition-colors",
  ),
  L3_VERTICAL_LINK_ON: "bg-primary/10 text-primary font-semibold",
  L3_VERTICAL_LINK_OFF: "text-muted-foreground hover:bg-primary/5 hover:text-foreground",
  BODY_MAIN: "min-h-0 min-w-0 flex-1",
  SECTION_STACK: "space-y-1.5 border-b border-border/60 pb-1.5 last:border-b-0 last:pb-0",
  SECTION_TAB:
    "flex w-full items-center rounded-lg px-3 pb-1.5 pt-2 text-left text-xs font-medium tracking-wider transition-colors",
  SECTION_TAB_ON: "text-primary",
  SECTION_TAB_OFF:
    "text-muted-foreground/95 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground dark:text-sidebar-foreground/65",
  L3_ASIDE_SECTION_HEAD: cn(
    "sticky top-0 z-[1] bg-card px-2.5 pb-1 pt-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground",
    "dark:bg-muted/20",
  ),
  SECTION_LEAF: "group flex items-center gap-3 rounded-lg px-3 py-2 text-[11px] font-medium transition-colors",
  SECTION_LEAF_ON: "bg-sidebar-accent text-sidebar-accent-foreground",
  SECTION_LEAF_OFF:
    "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
  SECTION_LEAF_ICON: "h-3.5 w-3.5 shrink-0 transition-colors",
  SECTION_LEAF_ICON_ON: "text-primary dark:text-sidebar-accent-foreground",
  SECTION_LEAF_ICON_OFF:
    "text-muted-foreground group-hover:text-primary dark:group-hover:text-sidebar-accent-foreground",
} as const;

/** Shared Tailwind class fragments for app shell (main chrome). Compose with `cn()` in layout components. */
export const APP_SHELL = {
  pageRoot: "surface-canvas",
  contentColumn: "lg:pl-64 flex flex-col",
  contentColumnCollapsed: "lg:pl-16 flex flex-col",
  mainCanvas: cn("min-h-0 min-w-0 surface-canvas", BASE_SPACING.PAGE_PADDING),
  sidebarAside: [
    "flex h-full flex-col overflow-hidden transition-[width] duration-300 ease-in-out",
    "bg-sidebar text-sidebar-foreground border-r border-sidebar-border",
  ].join(" "),
  sidebarAsideCollapsed: "w-16",
  sidebarAsideExpanded: "w-64",
  sidebarBrandMark:
    "brand-mark-frame h-10 w-10 shrink-0 overflow-hidden rounded-lg ring-2 ring-[rgb(var(--nice-gold-rgb)/0.35)] ring-offset-2 ring-offset-sidebar",
  sidebarBrandName: "sidebar-brand-name truncate text-sm font-bold leading-none tracking-tight",
  navLinkCollapsed: "lg:justify-center lg:gap-0 lg:px-2",
  navSectionLabel: "px-3 pb-1.5 text-xs font-medium tracking-tight text-muted-foreground dark:text-sidebar-foreground/65",
  navLinkBase: "group relative flex min-h-11 min-w-11 items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
  navLinkActive:
    "bg-sidebar-accent text-sidebar-accent-foreground before:absolute before:left-0 before:top-1/2 before:h-5 before:w-0.5 before:-translate-y-1/2 before:rounded-full before:bg-sidebar-primary dark:bg-sidebar-accent dark:text-sidebar-accent-foreground",
  navLinkInactive:
    "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
  navIconBase: "h-5 w-5 shrink-0 transition-colors",
  navIconActive: "text-sidebar-accent-foreground",
  navIconInactive: "text-sidebar-foreground group-hover:text-sidebar-accent-foreground",
  headerBar: ["sticky top-0 z-50 h-14 w-full border-b border-border bg-card"].join(" "),
  headerInner: cn("flex h-full items-center justify-between gap-x-2 sm:gap-x-4", BASE_SPACING.HEADER_PADDING),
  headerTitle: "truncate text-base font-semibold leading-tight tracking-tight text-foreground lg:text-lg",
  headerDescription: "truncate text-xs leading-tight text-muted-foreground",
  notificationHostOffset: "top-[calc(3.5rem+0.75rem)]",
  iconButtonMuted: "text-muted-foreground hover:bg-muted hover:text-foreground",
  userPill: "surface-control flex h-10 min-w-0 items-center gap-2 rounded-lg border px-2.5 py-1.5",
  userPillIcon: "h-4 w-4 text-muted-foreground",
  userPillText: "text-sm font-medium text-foreground",
  languageSwitcherOuter: "surface-panel flex items-center gap-1 rounded-lg border p-1",
  languageSwitcherActive: "surface-control text-foreground",
  languageSwitcherInactive: "text-muted-foreground hover:text-foreground",
} as const;

/** Full-screen auth routes: gold brand canvas, glass panel, and field chrome. */
export const AUTH_PAGE = {
  root: "relative min-h-screen overflow-hidden bg-background text-foreground",
  formShell: "dark relative flex min-h-screen flex-col items-center justify-center overflow-hidden p-4 sm:p-6 lg:p-10",
  glowLayer:
    ("pointer-events-none absolute inset-0" +
      "bg-[radial-gradient(circle_at_18%_-8%,rgb(var(--nice-gold-rgb)/0.14),transparent_38%),radial-gradient(circle_at_88%_12%,rgb(var(--nice-gold-light-rgb)/0.12),transparent_42%),radial-gradient(circle_at_50%_100%,rgb(var(--nice-gold-deep-rgb)/0.1),transparent_50%)]"),
  vignetteLayer: "pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgb(var(--background-rgb)/0.35),rgb(var(--background-rgb)/0.88))]",
  brandPanel: "auth-gold-canvas relative hidden min-h-screen flex-col overflow-hidden lg:flex lg:p-10 xl:p-14",
  brandHero: "relative z-10 flex min-h-screen flex-col gap-8 py-10 xl:gap-10 xl:py-14",
  brandPattern: "absolute inset-0 h-full w-full opacity-[0.12]",
  meshLayer: "login-mesh-drift pointer-events-none absolute inset-0 opacity-30",
  showcaseSection: "relative z-10 flex flex-1 flex-col gap-6",
  spotlightCard: "login-spotlight-card relative overflow-hidden rounded-2xl border border-[rgb(var(--nice-gold-light-rgb)/0.22)] bg-[rgb(42_30_4/0.55)] p-5 shadow-elevation2 backdrop-blur-sm sm:p-6",
  spotlightProgress: "login-progress-track mt-4 h-0.5 w-full overflow-hidden rounded-full bg-[rgb(var(--nice-gold-light-rgb)/0.15)]",
  spotlightProgressFill: "login-progress-fill h-full rounded-full bg-gradient-nice",
  bentoGrid: "grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-2.5",
  bentoTile:
    "login-bento-tile flex flex-col items-start gap-1.5 rounded-xl border border-[rgb(var(--nice-gold-light-rgb)/0.14)] bg-[rgb(42_30_4/0.4)] p-2.5 transition-[transform,box-shadow,border-color] duration-200 sm:p-3",
  bentoTileActive: "border-[rgb(var(--nice-gold-light-rgb)/0.45)] ring-1 ring-[rgb(var(--nice-gold-rgb)/0.35)] scale-[1.02] shadow-elevation1",
  formGlow: "auth-form-glow pointer-events-none absolute inset-0",
  formPanelEnter: "login-form-rise relative z-10 w-full max-w-md px-1",
  centerPy10: "relative min-h-screen flex justify-center items-center px-4 py-10",
  centerPy8: "relative min-h-screen flex justify-center items-center px-4 py-8",
  glassPanelWrap: "auth-glass-panel-wrap relative w-full max-w-md",
  glassPanel: "auth-glass-panel relative w-full rounded-2xl border p-8 backdrop-blur-xl sm:p-9",
  heading: "auth-form-heading text-2xl font-bold tracking-tight",
  headingBold: "text-2xl font-bold text-foreground tracking-tight",
  subtext: "auth-form-subtext mt-1 text-xs text-muted-foreground",
  fieldLabel: "mb-1.5 block text-sm font-semibold text-foreground",
  iconInField: "pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground",
  inputOnGlass: "auth-input-gold surface-control h-11 rounded-lg border pl-10 text-foreground placeholder:text-muted-foreground/95",
  inputOnGlassWithToggle: "auth-input-gold surface-control h-11 rounded-lg border pl-10 pr-10 text-foreground placeholder:text-muted-foreground/95",
  inputOnGlassSolo: "auth-input-gold surface-control h-11 rounded-lg border px-3 text-foreground placeholder:text-muted-foreground/95",
  submitButton: "btn-nice-gold h-12 w-full rounded-xl text-[0.9375rem] font-bold tracking-wide shadow-none hover:shadow-none",
  chromeControls: "absolute right-4 top-4 z-20 flex items-center gap-2 md:right-6 md:top-6",
  chromeNav: "mr-1 hidden items-center gap-1 sm:flex",
  chromeNavLink: "rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent/70 hover:text-foreground",
  chromeNavLinkActive: "bg-accent text-foreground",
  publicShell: "relative min-h-screen overflow-x-clip bg-background text-foreground",
  publicContent: cn(
    "public-stack relative z-10 mx-auto w-full max-w-screen-2xl px-4 pb-12 pt-16 md:px-8 md:pb-16 md:pt-20",
    BASE_SPACING.PAGE_SECTION_GAP_LARGE,
  ),
  linkSubtle: "text-xs text-muted-foreground hover:text-primary transition-colors",
  togglePassword: "absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground",
  callbackStatus: "text-sm text-muted-foreground text-center",
  callbackError: "text-sm text-destructive text-center",
  spotlightCrossfadeLayer: "login-spotlight-crossfade absolute inset-0",
  mobileSpotlightTrack: "flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
  mobileSpotlightSlide: "w-full shrink-0 snap-center",
} as const;

export const DASHBOARD_PAGE = {
  metricsGrid: "grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 xl:grid-cols-4 [&>*]:min-h-0 [&>*]:h-full",
  serviceGrid: "grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 xl:grid-cols-3 [&>*]:min-h-0 [&>*]:h-full",
  serviceCardTitle: "truncate text-base font-semibold text-foreground",
  performanceGrid: "grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 xl:grid-cols-3 [&>*]:min-h-0 [&>*]:h-full",
  commandRoot: "flex min-h-0 flex-col gap-3",
  commandHero: "flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between",
  /** Auto-fill pulses: avoid 6-col leftover (7th KPI) spilling into the next panel. */
  commandPulseGrid:
    "grid min-w-0 grid-cols-2 items-stretch gap-2 sm:grid-cols-3 xl:grid-cols-4 [&>*]:min-h-0 [&>*]:min-w-0",
  intakeStack: "flex w-full min-w-0 shrink-0 flex-col gap-4",
  intakeKpiGrid:
    "grid min-w-0 w-full grid-cols-2 items-stretch gap-2.5 [&>*]:min-h-0 [&>*]:min-w-0",
  intakePanelTwin:
    "grid min-w-0 w-full shrink-0 grid-cols-1 items-stretch gap-4 lg:grid-cols-2",
  intakePanelCard: "flex min-h-[14rem] w-full min-w-0 shrink-0 flex-col self-stretch overflow-hidden",
  commandSplit: "grid items-stretch gap-3 lg:grid-cols-[minmax(0,1.6fr)_minmax(14rem,0.85fr)] [&>*]:min-h-0 [&>*]:h-full",
  /** Solid white panel on muted canvas (Avalon-style — never muted/background gradient wells). */
  commandInsight: "relative min-h-[11rem] h-full overflow-hidden rounded-2xl border border-border surface-card",
  commandModuleRail: "flex flex-wrap gap-1.5",
} as const;

export function intakeKpiGridCols(slotCount: number): string {
  const n = Math.max(1, Math.min(7, Math.floor(slotCount)));
  if (n <= 2) return "sm:grid-cols-2";
  if (n === 3) return "sm:grid-cols-3";
  if (n === 4) return "sm:grid-cols-3 md:grid-cols-4";
  if (n === 5) return "sm:grid-cols-3 md:grid-cols-5";
  if (n === 6) return "sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-6";
  return "sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7";
}

export const MONITORING_PAGE = {
  root: WORKSPACE_LIST_PAGE.MAIN,
  /** Tighter than list BODY — pulse + status table share one viewport band. */
  body: cn("flex min-h-0 min-w-0 flex-col px-0 py-0 workspace-scroll-contain", BASE_SPACING.GAP_DEFAULT),
  toolbarRow: WORKSPACE_LIST_PAGE.HEADER_ROW,
  /** Eight equal pulses: 2×4 / 4×2 — avoid 6-col leftover row. */
  pulseGrid: "grid grid-cols-2 items-stretch gap-2 md:grid-cols-4 [&>*]:min-h-0 [&>*]:h-full",
  metricsGrid: DASHBOARD_PAGE.metricsGrid,
  performanceGrid: DASHBOARD_PAGE.performanceGrid,
  serviceGrid: DASHBOARD_PAGE.serviceGrid,
  serviceCardTitle: DASHBOARD_PAGE.serviceCardTitle,
} as const;
